import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getSession } from "../utils/session";
import { API_URL } from "../services/api";
import { FiPlayCircle, FiX } from "react-icons/fi";
import "./ContenidoPanel.css";

/*
 * Lo que el cliente ve: vídeos que su entrenador le recomienda, agrupados
 * por tema. El reproductor es el de YouTube y sólo se carga al pulsar, así
 * que la pantalla abre rápido y no se descarga nada de YouTube antes.
 */

const NOMBRES = {
  nutricion: "Nutrición",
  entrenamiento: "Técnica y entrenamiento",
  salud: "Salud",
  descanso: "Descanso y recuperación",
  titan: "De Titan Training",
};

const ORDEN = ["titan", "entrenamiento", "nutricion", "salud", "descanso"];

function Reproductor({ item, onCerrar }) {
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onCerrar();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onCerrar]);

  return (
    <div className="cn-modal" role="dialog" aria-modal="true" aria-label={item.titulo}>
      <div className="cn-modal-caja">
        <button type="button" className="cn-cerrar" onClick={onCerrar} aria-label="Cerrar">
          <FiX />
        </button>
        <div className="cn-video">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${item.youtubeId}?autoplay=1&rel=0`}
            title={item.titulo}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        </div>
        <div className="cn-modal-txt">
          <h3>{item.titulo}</h3>
          {item.canal && <p className="cn-canal">{item.canal}</p>}
          {item.porQue && <p className="cn-porque">{item.porQue}</p>}
          {item.descripcion && <p className="cn-desc">{item.descripcion}</p>}
        </div>
      </div>
    </div>
  );
}

function Tarjeta({ item, onAbrir }) {
  return (
    <button type="button" className="cn-tarjeta" onClick={() => onAbrir(item)}>
      <div className="cn-miniatura">
        {/* La miniatura la sirve YouTube; el vídeo sólo se carga al pulsar */}
        <img
          src={`https://i.ytimg.com/vi/${item.youtubeId}/hqdefault.jpg`}
          alt=""
          loading="lazy"
        />
        <span className="cn-play" aria-hidden="true">
          <FiPlayCircle />
        </span>
        {item.duracion && <span className="cn-duracion">{item.duracion}</span>}
      </div>
      <div className="cn-tarjeta-txt">
        <strong>{item.titulo}</strong>
        {item.canal && <span className="cn-canal">{item.canal}</span>}
        {item.porQue && <p className="cn-porque">{item.porQue}</p>}
      </div>
    </button>
  );
}

function ContenidoPanel() {
  const [lista, setLista] = useState(null);
  const [error, setError] = useState("");
  const [viendo, setViendo] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const { token } = await getSession();
      const res = await axios.get(`${API_URL}/contenido`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLista(res.data);
    } catch {
      setError("No se pudo cargar el contenido.");
      setLista([]);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const porCategoria = useMemo(() => {
    const grupos = {};
    for (const item of lista || []) {
      (grupos[item.categoria] ||= []).push(item);
    }
    return grupos;
  }, [lista]);

  if (lista === null) return <div className="app-estado">Cargando…</div>;

  if (!lista.length) {
    return (
      <section className="cn-vacio">
        <FiPlayCircle />
        <h2>Todavía no hay vídeos</h2>
        <p>Tu entrenador irá subiendo aquí vídeos de nutrición, técnica y salud.</p>
        {error && <p className="app-estado-error">{error}</p>}
      </section>
    );
  }

  const destacado = lista.find((x) => x.destacado);

  return (
    <div className="cn">
      {viendo && <Reproductor item={viendo} onCerrar={() => setViendo(null)} />}

      {destacado && (
        <section className="cn-destacado">
          <button type="button" onClick={() => setViendo(destacado)}>
            <div className="cn-destacado-img">
              <img
                src={`https://i.ytimg.com/vi/${destacado.youtubeId}/maxresdefault.jpg`}
                alt=""
                loading="eager"
                onError={(e) => {
                  // maxres no existe en todos los vídeos; hq sí
                  e.currentTarget.src = `https://i.ytimg.com/vi/${destacado.youtubeId}/hqdefault.jpg`;
                }}
              />
              <span className="cn-play cn-play-grande" aria-hidden="true">
                <FiPlayCircle />
              </span>
            </div>
            <div className="cn-destacado-txt">
              <span className="cn-eti">Recomendado esta semana</span>
              <h2>{destacado.titulo}</h2>
              {destacado.porQue && <p>{destacado.porQue}</p>}
            </div>
          </button>
        </section>
      )}

      {ORDEN.filter((c) => porCategoria[c]?.length).map((cat) => (
        <section key={cat} className="cn-bloque">
          <h2 className="cn-titulo">{NOMBRES[cat] || cat}</h2>
          <div className="cn-rejilla">
            {porCategoria[cat]
              .filter((x) => x !== destacado)
              .map((item) => (
                <Tarjeta key={item._id} item={item} onAbrir={setViendo} />
              ))}
          </div>
        </section>
      ))}

      {error && <p className="app-estado-error">{error}</p>}
    </div>
  );
}

export default ContenidoPanel;
